import React, { useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown, Spin, message as antdMessage } from "antd";
import {
  ArrowsClockwise,
  CaretDown,
  ListChecks,
  WarningCircle,
} from "@phosphor-icons/react";
import {
  fetchInboxTasks,
  selectInboxTaskMutationStatus,
  selectInboxTasksForDocument,
  updateInboxTaskStatusThunk,
} from "../../slice/InboxSlice";
import { getUserId } from "../../utils/fetchLocalStorage";
import {
  BriefingSkeletonBlock,
  BriefingSkeletonGroup,
  InboxItemActionButton,
  InboxItemActionGroup,
  InboxItemBody,
  InboxItemFooterRow,
  InboxItemMeta,
  InboxItemMetaPerson,
  InboxItemRow,
  InboxListBody,
  InboxListEmptyIconRing,
  InboxListEmptyState,
  InboxListErrorBanner,
  TASK_STATUS_LABELS,
  TaskStatusPill,
  UrgentTaskBadge,
} from "./AIPaneStyled";

/**
 * Phase 5c — per-document Tasks panel.
 *
 * Lazy-fetches `GET /api/inbox/items/<docId>/tasks/` on first
 * activation (via the `isActive` prop the AIPane threads through to
 * the active tab body). Subsequent activations of the same doc reuse
 * the cached list — task creates / status PATCHes patch the cache
 * in place from `InboxActionFooter` / inline status menus, so the
 * panel reflects the latest server state without a roundtrip.
 *
 * Per-row layout:
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ <body markdown-free text>                                │
 *   │                                                          │
 *   │ [Open]  Assigned to alice (by bob) · 3h ago    [Mark…]   │
 *   └──────────────────────────────────────────────────────────┘
 *
 * Status PATCH affordances are shown only when the current user is
 * the assignee or the assigner (the BE 403s otherwise — we just save
 * the round-trip). Allowed transitions are surfaced as an antd
 * dropdown menu; the BE accepts any of the four canonical statuses
 * (open / in_progress / completed / cancelled) on a single PATCH so
 * we surface them all and let the BE FSM enforce ordering.
 */

const ALL_STATUSES = ["open", "in_progress", "completed", "cancelled"];

const formatRelative = (iso) => {
  if (!iso) return "";
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return "";
  const deltaMs = Date.now() - ts;
  if (deltaMs < 0) return new Date(ts).toLocaleString();
  const sec = Math.floor(deltaMs / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString();
};

const personLabel = (user) =>
  (user && (user.full_name || user.username || user.email)) ||
  (user?.id != null ? `User #${user.id}` : "Unknown");

const TaskRow = ({ task, documentId, currentUserId }) => {
  const dispatch = useDispatch();
  const itemStatus = useSelector((state) =>
    selectInboxTaskMutationStatus(state, task?.id)
  );

  const isAssignee = currentUserId != null && task?.assigned_to?.id === currentUserId;
  const isAssigner = currentUserId != null && task?.assigned_by?.id === currentUserId;
  const canEdit = isAssignee || isAssigner;
  const busy = itemStatus === "loading";

  const menuItems = useMemo(
    () =>
      ALL_STATUSES.filter((s) => s !== task?.status).map((s) => ({
        key: s,
        label: TASK_STATUS_LABELS[s] || s,
      })),
    [task?.status]
  );

  const handleStatusChange = useCallback(
    ({ key }) => {
      if (!task?.id || busy) return;
      dispatch(
        updateInboxTaskStatusThunk({ documentId, taskId: task.id, status: key })
      )
        .unwrap()
        .catch((err) => {
          antdMessage.error(err?.message || "Couldn't update task status.");
        });
    },
    [busy, dispatch, documentId, task?.id]
  );

  return (
    <InboxItemRow>
      <InboxItemBody>
        {task?.priority === "urgent" && (
          <UrgentTaskBadge title="Urgent" aria-label="Urgent">
            !
          </UrgentTaskBadge>
        )}
        {task?.body || ""}
      </InboxItemBody>
      <InboxItemFooterRow>
        <InboxItemMeta>
          <TaskStatusPill $status={task?.status}>
            {TASK_STATUS_LABELS[task?.status] || task?.status || "Unknown"}
          </TaskStatusPill>
          <span>
            Assigned to{" "}
            <InboxItemMetaPerson>
              {personLabel(task?.assigned_to)}
            </InboxItemMetaPerson>
            {task?.assigned_by ? (
              <>
                {" by "}
                <InboxItemMetaPerson>
                  {personLabel(task?.assigned_by)}
                </InboxItemMetaPerson>
              </>
            ) : null}
          </span>
          <span>·</span>
          <span>{formatRelative(task?.created_at)}</span>
        </InboxItemMeta>
        {canEdit && menuItems.length > 0 && (
          <InboxItemActionGroup>
            <Dropdown
              menu={{ items: menuItems, onClick: handleStatusChange }}
              trigger={["click"]}
              disabled={busy}
            >
              <InboxItemActionButton
                type="button"
                disabled={busy}
                aria-label="Change task status"
              >
                {busy ? "Updating…" : "Change status"}
                <CaretDown size={10} weight="bold" />
              </InboxItemActionButton>
            </Dropdown>
          </InboxItemActionGroup>
        )}
      </InboxItemFooterRow>
    </InboxItemRow>
  );
};

TaskRow.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    body: PropTypes.string,
    status: PropTypes.string,
    created_at: PropTypes.string,
    assigned_to: PropTypes.object,
    assigned_by: PropTypes.object,
  }),
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentUserId: PropTypes.number,
};

const TasksTab = ({ documentId, isActive }) => {
  const dispatch = useDispatch();
  const slot = useSelector((state) =>
    selectInboxTasksForDocument(state, documentId)
  );
  const currentUserId = useMemo(() => getUserId(), []);

  // Lazy-fetch on first activation. We deliberately don't refetch on
  // every activation — the slice is patched in place by mutations, so
  // the cache stays correct without polling. If a sibling tab (an
  // open in-progress filings page, etc.) creates a task out-of-band,
  // the AIPane's tab switch handler can call fetchInboxTasks again
  // to reconcile.
  useEffect(() => {
    if (!isActive || documentId == null) return;
    if (slot.status !== "idle") return;
    dispatch(fetchInboxTasks({ documentId }));
  }, [dispatch, documentId, isActive, slot.status]);

  const handleManualRefresh = useCallback(() => {
    if (documentId == null) return;
    dispatch(fetchInboxTasks({ documentId }));
  }, [dispatch, documentId]);

  if (documentId == null) {
    return (
      <InboxListEmptyState>
        Select a document from the inbox to see its tasks.
      </InboxListEmptyState>
    );
  }

  const { status, items, error } = slot;
  const isLoading = status === "loading" && items.length === 0;
  const hasItems = items.length > 0;

  return (
    <>
      {error && (
        <InboxListErrorBanner role="alert">
          <WarningCircle size={14} weight="fill" style={{ marginRight: 6 }} />
          {error}
        </InboxListErrorBanner>
      )}
      <InboxListBody>
        {isLoading ? (
          <BriefingSkeletonGroup aria-label="Loading tasks">
            <BriefingSkeletonBlock $h={64} />
            <BriefingSkeletonBlock $h={64} />
            <BriefingSkeletonBlock $h={48} />
          </BriefingSkeletonGroup>
        ) : !hasItems ? (
          <InboxListEmptyState>
            <InboxListEmptyIconRing>
              <ListChecks size={28} weight="duotone" />
            </InboxListEmptyIconRing>
            <span>
              No tasks yet for this document.
              <br />
              Use <strong>Assign task</strong> below to delegate follow-up.
            </span>
            {status === "failed" && (
              <InboxItemActionButton
                type="button"
                onClick={handleManualRefresh}
              >
                <ArrowsClockwise size={14} weight="bold" /> Try again
              </InboxItemActionButton>
            )}
          </InboxListEmptyState>
        ) : (
          <>
            {status === "loading" && (
              <Spin
                size="small"
                style={{ alignSelf: "center", marginBottom: 4 }}
              />
            )}
            {items.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                documentId={documentId}
                currentUserId={currentUserId}
              />
            ))}
          </>
        )}
      </InboxListBody>
    </>
  );
};

TasksTab.propTypes = {
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isActive: PropTypes.bool,
};

export default TasksTab;
